import { useTranslation } from 'react-i18next';
import type {
  PatientJourneyDeepLinkTab,
  PatientJourneySnapshot,
  PatientJourneyStage,
} from '@/application/metrics/patientJourney';
import { isPatientJourneyEmpty, visiblePatientJourneyStages } from '@/application/metrics/patientJourney';
import { HelpTooltip } from '@/presentation/components/common/HelpTooltip';
import { formatInteger, formatPercent } from '@/presentation/components/metrics/metricFormatters';

interface PatientJourneyPipelineProps {
  snapshot: PatientJourneySnapshot;
  locale: string;
  riskDesde?: number;
  riskHasta?: number;
  onStageSelect?: (tab: PatientJourneyDeepLinkTab) => void;
}

const STAGE_TONE: Record<PatientJourneyStage['id'], string> = {
  nuevo: 'border-l-blue-500',
  regular: 'border-l-teal-500',
  en_riesgo: 'border-l-amber-500',
  no_volvio: 'border-l-red-500',
};

/**
 * Embudo de overview: Nuevo → Regular → En riesgo → No volvió (subset según permisos).
 */
export function PatientJourneyPipeline({
  snapshot,
  locale,
  onStageSelect,
}: PatientJourneyPipelineProps) {
  const { t } = useTranslation();
  const stages = visiblePatientJourneyStages(snapshot);
  const empty = isPatientJourneyEmpty(snapshot);

  if (stages.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3" data-testid="patients-journey-pipeline" aria-label={t('domain.patients.journey.title')}>
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('domain.patients.journey.title')}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-300">{t('domain.patients.journey.hint')}</p>
      </div>

      <ol className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {stages.map((stage, index) => (
          <li key={stage.id} className="min-w-0">
            <JourneyStageCard
              stage={stage}
              locale={locale}
              index={index}
              total={stages.length}
              onSelect={stage.deepLinkTab && onStageSelect ? () => onStageSelect(stage.deepLinkTab!) : undefined}
            />
          </li>
        ))}
      </ol>

      {empty ? (
        <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="patients-journey-empty">
          {t('domain.patients.journey.empty')}
        </p>
      ) : null}
    </section>
  );
}

function JourneyStageCard({
  stage,
  locale,
  index,
  total,
  onSelect,
}: {
  stage: PatientJourneyStage;
  locale: string;
  index: number;
  total: number;
  onSelect?: () => void;
}) {
  const { t } = useTranslation();
  const label = t(`domain.patients.journey.stages.${stage.id}.label`);
  const definition = t(stage.definitionKey);
  const whatIs = t('domain.patients.journey.whatIsAria', { stage: label });
  const value = stage.count == null ? '—' : formatInteger(stage.count, locale);

  return (
    <article
      data-testid={`patients-journey-stage-${stage.id}`}
      className={`h-full min-w-0 rounded-md border border-l-4 border-gray-200 ${STAGE_TONE[stage.id]} bg-white px-3 py-2.5 text-left shadow-sm dark:border-gray-800 dark:bg-gray-900`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {index + 1}. {label}
        </p>
        <HelpTooltip content={definition} ariaLabel={whatIs} size="sm" stopPropagation align="end" />
      </div>
      <p className="mt-1 text-2xl font-bold tabular-nums text-gray-950 dark:text-white">{value}</p>
      {stage.id === 'regular' && stage.ratePct != null ? (
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {t('domain.patients.journey.rateLine', { value: formatPercent(stage.ratePct, locale) })}
        </p>
      ) : null}
      {onSelect ? (
        <button
          type="button"
          onClick={onSelect}
          className="mt-2 text-xs font-semibold text-teal-700 hover:underline dark:text-teal-300"
        >
          {t('domain.patients.journey.cta')} →
        </button>
      ) : null}
      {index < total - 1 ? <span className="sr-only">{t('domain.patients.journey.then')}</span> : null}
    </article>
  );
}
